import { z } from 'zod';
import { IsoTime } from './basics.js';
/**
 * Dashboard roles and the user record (knowledge doc sections 16 and 17). Distinct
 * from `Role` in `settings.ts`, which names what a *server* runs; this names what a
 * *person* may do.
 *
 * The three are ordered, so a check is "at least this role" rather than a set
 * membership test.
 */
export const UserRole = z.enum(['viewer', 'operator', 'admin']);
const RANK = { viewer: 0, operator: 1, admin: 2 };
/** True when `role` is `required` or above. Admin therefore passes every check. */
export function roleAtLeast(role, required) {
    return RANK[role] >= RANK[required];
}
/**
 * Section 17: high-risk actions re-check the password or a 2FA code. The window is
 * short on purpose — it exists so an unattended session cannot revoke a server key.
 */
export const REAUTH_MAX_AGE_SECONDS = 300;
export const UserId = z.uuid();
/**
 * Lowercased and trimmed before validation, because the database holds one row per
 * address and `Admin@example.com` must not be able to become a second account. The
 * `users` table carries a CHECK for the same rule, so a path that skips this schema
 * cannot store a mixed-case address either.
 *
 * 254 is the longest address SMTP will carry.
 */
export const Email = z.string().trim().toLowerCase().pipe(z.email().max(254));
/**
 * Length only, deliberately. Composition rules ("one digit, one symbol") push people
 * towards `Password1!` and NIST SP 800-63B advises against them; length is what
 * actually costs an attacker. The upper bound exists because the password is fed to
 * a memory-hard hash, and an unbounded one is a way to make the API do unbounded
 * work per login attempt.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 200;
export const Password = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);
/**
 * An IANA zone name, checked against the runtime's own tz database rather than a
 * list we would have to maintain. Section 20: UTC on the wire and in storage, and
 * the dashboard is the only place that converts.
 */
export function isSupportedTimezone(value) {
    try {
        // Called rather than constructed: the return value is discarded either way, and
        // a bare `new` is an unused expression.
        Intl.DateTimeFormat('en-US', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
export const Timezone = z
    .string()
    .min(1)
    .max(64)
    .refine(isSupportedTimezone, { message: 'not a time zone this system knows' });
/**
 * What the API tells the browser about a person. Section 16's `users` row minus
 * `password_hash` and `totp_secret_enc`: rule 5 keeps both out of every response,
 * and the shape is the enforcement rather than the intention, since the route
 * serializes through this schema.
 *
 * `timezone` is null when the user has not chosen one, and the dashboard falls back
 * to the instance default the API reports beside it.
 */
export const User = z.object({
    id: UserId,
    email: z.email(),
    role: UserRole,
    /** Whether an authenticator app is in force for this account (section 17). */
    totpEnabled: z.boolean(),
    timezone: Timezone.nullable(),
    /** Set rather than deleted, so audit entries and event assignments keep a name. */
    disabledAt: IsoTime.nullable(),
    lastLoginAt: IsoTime.nullable(),
    createdAt: IsoTime,
});
//# sourceMappingURL=user.js.map